import { useEffect, useState } from 'react';
import { fetchSupplierOrderPZDocuments } from '../api/supplierOrdersApi';
import { searchPurchaseInvoiceDrafts } from '../../invoices/api/purchaseInvoiceImportApi';

export interface SupplierOrderStageData {
  /** Numbers of the received PZ documents of this ZD (null = loading / failed). */
  pzNumbers: string[] | null;
  /** Numbers of the purchase invoices of this ZD (null = loading / failed / not visible). */
  invoiceNumbers: string[] | null;
  /** The same approved purchase invoices with ids, so the card can reopen one in its invoice window. */
  invoices: SupplierOrderStageInvoice[] | null;
}

export interface SupplierOrderStageInvoice {
  id: string;
  number: string;
}

interface Options {
  supplierOrderId: string | undefined;
  /** Purchase invoices are visible to this viewer (`canManagePurchaseInvoiceImports`). */
  canReadInvoices: boolean;
  /** Changes whenever the ZD is reloaded (status, items), so the lists follow. */
  revision: unknown;
}

/**
 * What the stage panel of the ZD card needs besides the order itself: the
 * received PZ documents and the purchase invoices (the task list says
 * „przyjęty (PZ)” and „Faktura zakupu”). Both are advisory: a failure leaves
 * null, never blocks the card. The trucks come from the transport section
 * (fetched once there).
 */
export default function useSupplierOrderStageData({ supplierOrderId, canReadInvoices, revision }: Options): SupplierOrderStageData {
  const [pzNumbers, setPzNumbers] = useState<string[] | null>(null);
  const [invoices, setInvoices] = useState<SupplierOrderStageInvoice[] | null>(null);

  useEffect(() => {
    if (!supplierOrderId) return undefined;
    let active = true;
    fetchSupplierOrderPZDocuments(supplierOrderId)
      .then((documents) => {
        if (active) setPzNumbers(documents.filter((document) => String(document.status) === 'RECEIVED').map((document) => document.pzNumber));
      })
      .catch(() => { if (active) setPzNumbers(null); });
    if (canReadInvoices) {
      searchPurchaseInvoiceDrafts({ supplierOrderId, limit: 20, offset: 0 })
        .then((rows) => {
          if (active) setInvoices(rows.filter((row) => row.status === 'APPROVED' && row.id).map((row) => ({ id: row.id!, number: row.originalDocumentNumber || '—' })));
        })
        .catch(() => { if (active) setInvoices(null); });
    }
    return () => { active = false; };
  }, [supplierOrderId, canReadInvoices, revision]);

  return { pzNumbers, invoiceNumbers: invoices ? invoices.map((invoice) => invoice.number) : null, invoices };
}
